import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useSelector } from 'react-redux';

import { selectClients } from '@client/model/selectors';
import { advisorOnboardingApi, advisorOnboardingKeys } from '@client/api/waterlily';

export const ONBOARDING_STEPS: readonly AdvisorOnboardingTrackingStep[] = [
  'basic-info',
  'activate-seat',
  'consent',
  'welcome',
  'invite-client',
  'client-intake-form',
  'complete-profile',
  'review-results',
];

const CLIENT_TRIGGERED_STEPS: readonly AdvisorOnboardingClientTriggeredStep[] = [
  'welcome',
  'review-results',
];

function isClientTriggeredStep(step: string): step is AdvisorOnboardingClientTriggeredStep {
  return CLIENT_TRIGGERED_STEPS.includes(step as AdvisorOnboardingClientTriggeredStep);
}

export function useAdvisorOnboarding(advisorId: string) {
  const queryClient = useQueryClient();
  const clients = useSelector(selectClients);

  // Fetch onboarding status using React Query
  // Server computes data-backed steps and merges with stored client-triggered steps
  const {
    data: onboardingStatus,
    isLoading,
    isError,
    refetch,
  } = useQuery({
    queryKey: advisorOnboardingKeys.status(advisorId),
    queryFn: () => advisorOnboardingApi.getStatus(advisorId),
    enabled: Boolean(advisorId),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  // Mutation for marking client-triggered steps as complete
  const markStepMutation = useMutation({
    mutationFn: (step: AdvisorOnboardingClientTriggeredStep) =>
      advisorOnboardingApi.markStepComplete({ advisorId, step }),
    onSuccess: result => {
      // Update cache with the new completed steps from server
      queryClient.setQueryData(
        advisorOnboardingKeys.status(advisorId),
        (oldData: AdvisorOnboardingStatus | undefined) => {
          if (!oldData) return oldData;
          return {
            ...oldData,
            completedSteps: {
              ...oldData.completedSteps,
              completedSteps: result.completedSteps,
            },
          };
        },
      );
    },
  });

  const checklist = onboardingStatus?.checklist ?? null;
  const completedStepsData = onboardingStatus?.completedSteps ?? null;
  const completedSteps = completedStepsData?.completedSteps ?? [];

  const clientCount = clients?.length ?? 0;
  const hasCompletedAllSteps =
    completedSteps.length > 0 && ONBOARDING_STEPS.every(step => completedSteps.includes(step));

  /**
   * Mark a client-triggered step as complete.
   * Only works for 'welcome' and 'review-results' steps.
   * Data-backed steps are computed server-side automatically.
   */
  const markStepAsCompleted = useCallback(
    (stepId: string) => {
      if (!advisorId) return;
      if (!isClientTriggeredStep(stepId)) {
        // Data-backed steps are computed server-side, just refetch
        refetch();
        return;
      }
      if (!completedSteps.includes(stepId)) {
        markStepMutation.mutate(stepId);
      }
    },
    [advisorId, completedSteps, markStepMutation, refetch],
  );

  const isStepCompleted = useCallback(
    (stepId: string) => {
      return completedSteps.includes(stepId);
    },
    [completedSteps],
  );

  return {
    checklist,
    completedSteps,
    completedStepsData,
    isLoading,
    isError,
    isComplete: !isLoading && !isError && Boolean(onboardingStatus),
    refetch,
    markStepAsCompleted,
    isStepCompleted,
    clientCount,
    hasCompletedAllSteps,
  };
}
